import { Container } from 'pixi.js';
import { onViewportChange, setActiveOrientation } from './viewport';

/**
 * Base class for full-screen scenes. Subclasses implement onEnter()/onLeave() and register cleanup
 * via track() (e.g. message-handler unsubscribes), which runs automatically when the scene leaves.
 */
export abstract class Scene extends Container {
  private disposers: Array<() => void> = [];

  /**
   * Desired render orientation. Portrait by default; `BackgammonTableScene` overrides to 'landscape'
   * so PixiApp rotates the world 90° (or renders native landscape) to fill the screen. Read by
   * SceneManager on each swap; must be a constant per scene (no runtime flips).
   */
  get orientation(): 'portrait' | 'landscape' {
    return 'portrait';
  }

  /** Register a cleanup function to run when this scene leaves (unsubscribe handlers, timers, …). */
  protected track(dispose: () => void): void {
    this.disposers.push(dispose);
  }

  onEnter(): void {}
  onLeave(): void {}

  /**
   * Called when the design-space viewport height changes (resize / orientation / Telegram fullscreen,
   * which expands *after* boot). Override to re-flow against the new `viewport.h`; default no-op so
   * scenes that don't care are unaffected.
   */
  onResize(): void {}

  // Called by SceneManager — do not override.
  enter(): void {
    this.onEnter();
  }
  leave(): void {
    this.onLeave();
    for (const d of this.disposers) d();
    this.disposers = [];
  }
}

/** Swaps the active scene, running enter/leave lifecycle and destroying the previous one. */
export class SceneManager {
  private current: Scene | null = null;
  private readonly changeHandlers = new Set<(scene: Scene) => void>();

  /** `relayout` re-letterboxes synchronously (PixiApp.relayout); optional so tests can omit it. */
  constructor(
    private readonly root: Container,
    private readonly relayout?: () => void,
  ) {
    // Re-flow the active scene when the design-space viewport changes (Telegram fullscreen / rotation).
    onViewportChange(() => this.current?.onResize());
  }

  show(scene: Scene): void {
    const prev = this.current;
    // Publish the incoming scene's orientation and re-letterbox *before* it becomes current, so its
    // onEnter() reads the correct viewport. Doing it while `prev` is still current means the relayout's
    // viewport-change callback (if any) lands on the outgoing scene — never on the un-entered new one.
    setActiveOrientation(scene.orientation);
    this.relayout?.();

    this.current = scene;
    this.root.addChild(scene);
    scene.enter();
    for (const handler of [...this.changeHandlers]) handler(scene);
    if (prev) {
      prev.leave();
      this.root.removeChild(prev);
      prev.destroy({ children: true });
    }
  }

  get active(): Scene | null {
    return this.current;
  }

  onChange(handler: (scene: Scene) => void): () => void {
    this.changeHandlers.add(handler);
    return () => this.changeHandlers.delete(handler);
  }
}
